/**
 * VerifyHub — хаб верификаций (Flow step="verify").
 *  1) Верификация личности (KYC) — доступна всегда.
 *  2) ИПДС (источник средств) + шкала лимита вывода — показываются ТОЛЬКО после того, как клиент
 *     хотя бы раз упёрся в лимит (limits.limit_reached, латч). До этого раздел лимитов скрыт.
 * Пропсы: kycState, onKyc (открыть KYC), onSof (открыть ИПДС), onBack.
 */
import { useEffect, useState, type ReactNode } from "react";
import { useBackButton } from "@/telegram/useMainButton";
import { Screen } from "@/components/Screen";
import { Badge } from "@/components/Badge";
import { sofApi, type SofOverview } from "@/lib/api";
import type { KycState } from "@/screens/Profile";
import { formatUsdt } from "@/lib/format";

type Props = { kycState: KycState; onKyc: () => void; onSof: () => void; onBack: () => void };

function Tier({
  icon,
  title,
  subtitle,
  badge,
  onClick,
  locked,
}: {
  icon: string;
  title: string;
  subtitle: string;
  badge: ReactNode;
  onClick?: () => void;
  locked?: boolean;
}) {
  const clickable = !!onClick && !locked;
  return (
    <button
      onClick={clickable ? onClick : undefined}
      disabled={!clickable}
      className={`flex w-full items-center gap-3 rounded-2xl border border-white/[0.06] bg-surface-card px-4 py-4 text-left ${
        clickable ? "active:bg-white/[0.03]" : "opacity-80"
      } ${locked ? "grayscale" : ""}`}
    >
      <span className="grid h-11 w-11 shrink-0 place-items-center rounded-xl bg-brand/15 text-[20px]">{locked ? "🔒" : icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[15px] font-medium">{title}</span>
        <span className="block text-[13px] text-white/45">{subtitle}</span>
      </span>
      <span className="shrink-0">{badge}</span>
    </button>
  );
}

function monthResetLabel(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString("ru-RU", { day: "numeric", month: "long" });
  } catch {
    return "";
  }
}

function LimitScale({ ov }: { ov: SofOverview }) {
  const l = ov.limits;
  const monthPct = l.monthly_limit_usd > 0 ? Math.min(100, (l.monthly_used_usd / l.monthly_limit_usd) * 100) : 0;
  const addlRemaining = l.addl_remaining_usd;
  const addlPct = l.addl_total_usd > 0 ? Math.min(100, (l.addl_used_usd / l.addl_total_usd) * 100) : 0;
  return (
    <div className="rounded-2xl border border-white/[0.06] bg-surface-card p-4">
      <div className="flex items-baseline justify-between">
        <span className="text-[13px] text-white/55">Месячный лимит</span>
        <span className="text-[13px] text-white/45">обновится {monthResetLabel(l.month_reset_at)}</span>
      </div>
      <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/[0.08]">
        <div className="h-full rounded-full bg-brand" style={{ width: `${monthPct}%` }} />
      </div>
      <div className="mt-1.5 text-[13px]">
        <span className="font-medium text-white">{formatUsdt(l.monthly_used_usd)}</span>
        <span className="text-white/45"> из {formatUsdt(l.monthly_limit_usd)}</span>
      </div>

      {l.addl_total_usd > 0 && (
        <>
          <div className="mt-4 flex items-baseline justify-between">
            <span className="text-[13px] text-white/55">Дополнительный лимит</span>
            <span className="text-[13px] text-white/45">осталось {formatUsdt(addlRemaining)}</span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/[0.08]">
            <div className="h-full rounded-full bg-brand/70" style={{ width: `${addlPct}%` }} />
          </div>
          <div className="mt-1.5 text-[13px]">
            <span className="font-medium text-white">{formatUsdt(l.addl_used_usd)}</span>
            <span className="text-white/45"> из {formatUsdt(l.addl_total_usd)}</span>
          </div>
        </>
      )}
    </div>
  );
}

export function VerifyHub({ kycState, onKyc, onSof, onBack }: Props) {
  const [ov, setOv] = useState<SofOverview | null>(null);
  useEffect(() => {
    sofApi.overview().then(setOv).catch(() => setOv(null));
  }, []);
  useBackButton(onBack);

  const kycActionable = kycState === "none" || kycState === "rejected";
  const kycBadge =
    kycState === "approved" ? (
      <Badge tone="ok">Пройдена ✓</Badge>
    ) : kycState === "pending" ? (
      <Badge tone="warn">На проверке</Badge>
    ) : kycState === "rejected" ? (
      <Badge tone="err">Отклонена ›</Badge>
    ) : (
      <Badge tone="muted">Не пройдена ›</Badge>
    );

  const limitReached = !!ov?.limits.limit_reached;
  const pendingCount = ov?.applications.filter((a) => a.status === "pending").length ?? 0;
  const sofBadge =
    pendingCount > 0 ? <Badge tone="warn">На проверке</Badge> : <Badge tone="muted">Открыть ›</Badge>;

  return (
    <Screen title="Верификация" subtitle="Личность и, при необходимости, источник средств">
      <div className="space-y-3">
        <Tier
          icon="🪪"
          title="Верификация личности"
          subtitle="Паспорт и селфи"
          badge={kycBadge}
          onClick={kycActionable ? onKyc : undefined}
        />

        {limitReached && ov && (
          <>
            <LimitScale ov={ov} />
            <Tier
              icon="💼"
              title="Источник происхождения денежных средств"
              subtitle="Увеличить доступный лимит вывода"
              badge={sofBadge}
              onClick={onSof}
            />
          </>
        )}
      </div>
    </Screen>
  );
}
